/**
 * Executive header: overall score, risk exposure, top 5 priorities, quick wins, what we could not measure (summary
 * section) and the six signal scores with grade and coverage (signals section).
 */
import { Badge, Card, CardHead, ScoreRing, Text } from '@qm/ui';
import { SIGNAL_IDS } from '@qm/contracts/audit-analysis';
import type { AdvancedFinding, AdvancedReport } from '@qm/contracts/audit-remediation';
import { overallScore, progressTotals, quickWins, riskExposure, topPriorities } from './model';
import { L, useReportText } from './text';
import { gradeTone, severityTone } from './tones';

export function SummarySection({ report, onOpen }: { report: AdvancedReport; onOpen: (id: string) => void }) {
  const { t, num } = useReportText();
  const overall = overallScore(report);
  const risk = riskExposure(report.findings);
  const totals = progressTotals(report.findings);
  const top = topPriorities(report.findings, 5);
  const wins = quickWins(report.findings);
  return (
    <Card aria-labelledby="ar-summary" className="qm-ar-summary">
      <CardHead id="ar-summary" title={t('audit-remediation.report.summary.title')} sub={t('audit-remediation.report.summary.sub', { count: num(report.findings.length) })} />
      <div className="qm-ar-summary__stats">
        <div className="qm-ar-stat">
          {overall === null ? <Text>{t('audit-remediation.report.notMeasured')}</Text>
            : <ScoreRing value={overall} label={t('audit-remediation.report.summary.overall')} size="lg" />}
        </div>
        <div className="qm-ar-stat" data-risk={risk.level}>
          <p className="qm-ar-stat__label">{t('audit-remediation.report.risk.title')}</p>
          <p className="qm-ar-stat__value">{num(risk.score)}<small>/100</small></p>
          <Badge tone={risk.level === 'severe' ? 'error' : risk.level === 'elevated' ? 'warning' : risk.level === 'moderate' ? 'accent' : 'success'}>{t(`audit-remediation.report.risk.${risk.level}`)}</Badge>
          <p className="qm-ar-stat__note">{t('audit-remediation.report.risk.detail', { critical: num(risk.counts.critical), high: num(risk.counts.high), regulatory: num(risk.regulatory), accepted: num(risk.accepted) })}</p>
        </div>
        <div className="qm-ar-stat">
          <p className="qm-ar-stat__label">{t('audit-remediation.report.summary.progress')}</p>
          <p className="qm-ar-stat__value">{num(totals.resolved)}<small>/{num(totals.total)}</small></p>
          <p className="qm-ar-stat__note">{t('audit-remediation.report.summary.progressDetail', { open: num(totals.open), inProgress: num(totals.inProgress), verified: num(totals.verified) })}</p>
        </div>
      </div>

      <div className="qm-ar-summary__lists">
        <div>
          <h3 className="qm-ar-h3">{t('audit-remediation.report.summary.top')}</h3>
          {top.length ? <ol className="qm-ar-mini">{top.map((f) => <MiniFinding key={f.id} f={f} onOpen={onOpen} />)}</ol>
            : <Text tone="muted">{t('audit-remediation.report.summary.allDone')}</Text>}
        </div>
        <div>
          <h3 className="qm-ar-h3">{t('audit-remediation.report.summary.quickWins')}</h3>
          {wins.length ? <ul className="qm-ar-mini">{wins.slice(0, 5).map((f) => <MiniFinding key={f.id} f={f} onOpen={onOpen} />)}</ul>
            : <Text tone="muted">{t('audit-remediation.report.summary.noQuickWins')}</Text>}
        </div>
        <div>
          <h3 className="qm-ar-h3">{t('audit-remediation.report.summary.notMeasured')}</h3>
          {report.coverage.notMeasured.length ? (
            <ul className="qm-ar-mini">
              {report.coverage.notMeasured.map((n) => <li key={n.ref}><code dir="ltr">{n.ref}</code> <span lang="en" dir="auto">{n.reason}</span></li>)}
            </ul>
          ) : <Text tone="muted">{t('audit-remediation.report.summary.allMeasured')}</Text>}
        </div>
      </div>
    </Card>
  );
}

function MiniFinding({ f, onOpen }: { f: AdvancedFinding; onOpen: (id: string) => void }) {
  const { t, num } = useReportText();
  return (
    <li className="qm-ar-mini__item">
      <Badge tone={severityTone(f.severity)}>{t(`audit-remediation.severity.${f.severity}`)}</Badge>
      <button type="button" className="qm-ar-link" aria-haspopup="dialog" onClick={() => onOpen(f.id)}><L v={f.title} /></button>
      <span className="qm-ar-muted">{t('audit-remediation.report.priorityShort', { n: num(f.priority) })}</span>
    </li>
  );
}

export function SignalsSection({ report }: { report: AdvancedReport }) {
  const { t, num, pct } = useReportText();
  return (
    <Card aria-labelledby="ar-signals">
      <CardHead id="ar-signals" title={t('audit-remediation.report.signals.title')} sub={t('audit-remediation.report.signals.sub')} />
      <ul className="qm-ar-signals">
        {SIGNAL_IDS.map((id) => {
          const s = report.signals.find((x) => x.id === id);
          const cov = report.coverage.bySignal[id];
          return (
            <li key={id} className="qm-ar-signal">
              <div className="qm-ar-signal__head">
                <b>{t(`audit-remediation.report.signal.${id}`)}</b>
                <Badge tone={gradeTone(s?.grade ?? 'not_measured')}>
                  {!s || s.score === null ? t('audit-remediation.report.notMeasured') : t('audit-remediation.report.signals.scoreGrade', { score: num(s.score), grade: s.grade })}
                </Badge>
              </div>
              <div className="qm-ar-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(cov * 100)}
                aria-label={t('audit-remediation.report.signals.coverageOf', { signal: t(`audit-remediation.report.signal.${id}`) })}>
                <span style={{ inlineSize: `${Math.round(cov * 100)}%` }} />
              </div>
              <p className="qm-ar-muted">{t('audit-remediation.report.signals.coverage', { pct: pct(cov) })} · {t('audit-remediation.report.signals.findings', { n: num(s?.findingCount ?? 0) })}</p>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function CoverageSection({ report }: { report: AdvancedReport }) {
  const { t, num, pct } = useReportText();
  return (
    <Card aria-labelledby="ar-coverage" id="ar-coverage-card">
      <CardHead id="ar-coverage" title={t('audit-remediation.report.coverage.title')} sub={t('audit-remediation.report.coverage.sub', { pct: pct(report.coverage.overall), pages: num(report.coverage.pagesFetched) })} />
      {report.coverage.notMeasured.length ? (
        <ul className="qm-ar-list">
          {report.coverage.notMeasured.map((n) => <li key={n.ref}><code dir="ltr">{n.ref}</code><span lang="en" dir="auto">{n.reason}</span></li>)}
        </ul>
      ) : <Text tone="muted">{t('audit-remediation.report.summary.allMeasured')}</Text>}
    </Card>
  );
}

export function CompetitorsSection({ report }: { report: AdvancedReport }) {
  const { t, num } = useReportText();
  if (!report.competitors.length) return null;
  return (
    <Card aria-labelledby="ar-competitors">
      <CardHead id="ar-competitors" title={t('audit-remediation.report.competitors.title')} />
      <div className="qm-table-wrap">
        <table className="qm-table">
          <caption className="qm-sr-only">{t('audit-remediation.report.competitors.title')}</caption>
          <thead><tr>
            <th scope="col">{t('audit-remediation.report.competitors.name')}</th>
            <th scope="col" data-numeric="true">{t('audit-remediation.report.summary.overall')}</th>
            {SIGNAL_IDS.map((id) => <th key={id} scope="col" data-numeric="true">{t(`audit-remediation.report.signal.${id}`)}</th>)}
            <th scope="col">{t('audit-remediation.report.competitors.edge')}</th>
          </tr></thead>
          <tbody>
            <tr className="qm-ar-you">
              <th scope="row" data-label={t('audit-remediation.report.competitors.name')}><bdi>{report.subject.domain ?? report.subject.businessName}</bdi> ({t('audit-remediation.report.competitors.you')})</th>
              <td data-numeric="true" data-label={t('audit-remediation.report.summary.overall')}>{fmt(overallScoreOf(report), num, t)}</td>
              {SIGNAL_IDS.map((id) => <td key={id} data-numeric="true" data-label={t(`audit-remediation.report.signal.${id}`)}>{fmt(report.signals.find((s) => s.id === id)?.score ?? null, num, t)}</td>)}
              <td data-label={t('audit-remediation.report.competitors.edge')} />
            </tr>
            {report.competitors.map((c) => (
              <tr key={c.name}>
                <th scope="row" data-label={t('audit-remediation.report.competitors.name')}><bdi>{c.domain ?? c.name}</bdi></th>
                <td data-numeric="true" data-label={t('audit-remediation.report.summary.overall')}>{fmt(c.overall, num, t)}</td>
                {SIGNAL_IDS.map((id) => <td key={id} data-numeric="true" data-label={t(`audit-remediation.report.signal.${id}`)}>{fmt(c.signals[id], num, t)}</td>)}
                <td data-label={t('audit-remediation.report.competitors.edge')}><L v={c.edge} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const overallScoreOf = (r: AdvancedReport) => overallScore(r);
const fmt = (v: number | null, num: (n: number) => string, t: (k: string) => string) => (v === null ? t('audit-remediation.report.notMeasuredShort') : num(v));
